import React from "react"
import { Box, Paper } from "@mui/material"
import { makeStyles } from "makeStyles"
import Rating from "@mui/material/Rating"

import Link from "components/i18n/Link"
import Price from "components/Price"
import LazyLoadedImage from "components/LazyLoadedImage"
import ProductHitStructuredData from "@secureo/common/components/StructuredData/ProductHitStructuredData"

import useProductHitImpressionTracking from "@secureo/common/hooks/tracking/useProductHitImpressionTracking"

import getDeliveryTimeWorkDaysString from "@secureo/common/utils/commerceTools/formatters/getDeliveryTimeWorkDaysString"
import { trackProductHitClick } from "@secureo/common/utils/tracking/productHit"

import { AlgoliaProductHit } from "@secureo/common/typings/Algolia"

const useStyles = makeStyles()({
	title: {
		color: "black",
		cursor: "pointer",
		height: 40,
		width: "100%",
	},
	product: {
		boxShadow: "none",
		borderRadius: 10,
		transition: "0.3s",
		border: "1px solid #E3E5E6",
		"&:hover": {
			border: "1px solid #4691CE",
			boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.35)",
			"& $button": {
				background: "#4691CE",
				color: "white",
				boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.35)",
			},
			"& $title": {
				color: "#4691CE",
			},
		},
		"@media only screen and (max-width: 600px)": {
			borderRadius: 0,
			height: "100%",
		},
	},
})

const MobileViewItem = ({ hit, locale, position, listName = "", categoryName }: Props) => {
	const { classes } = useStyles()
	const trackProductHitImpression = useProductHitImpressionTracking(
		hit,
		categoryName,
		listName || categoryName,
		position,
	)

	const {
		name,
		grossPriceCents,
		image,
		deliveryTimeWorkdays,
		slug,
		manufacturer,
		rating = 0,
	} = hit

	const cutProductName = (name = "") => (name.length > 53 ? `${name.substring(0, 53)} ...` : name)

	const onClick = () => {
		trackProductHitClick(
			name,
			grossPriceCents,
			manufacturer,
			categoryName,
			listName || categoryName,
			position,
		)
	}

	return (
		<Link href={`/p/${slug}`} onClick={onClick}>
			<div style={{ position: "relative" }}>
				<Paper className={classes.product}>
					<Box p={2} style={{ display: "flex", flexDirection: "column" }}>
						{/* Product Image */}
						<div>
							<Box style={{ textAlign: "center" }}>
								<LazyLoadedImage
									alt={name}
									title={name}
									src={image}
									style={{
										height: 82,
										maxWidth: "100%",
										objectFit: "contain",
										width: "100%",
										margin: "auto",
									}}
									onIntersection={trackProductHitImpression}
								/>
							</Box>
						</div>
						<div>
							{/* Rating */}
							<Box>
								<Rating
									name="read-only"
									value={rating}
									precision={0.5}
									size="small"
									readOnly
								/>
							</Box>
							{/* Product Name */}

							<Box className={classes.title} style={{ fontSize: 12 }}>
								<strong>{cutProductName(name)}</strong>
							</Box>
						</div>
					</Box>
					<Box p={2} style={{ flex: "1 0 auto" }}>
						{/* Price */}
						<strong style={{ fontSize: 14, marginRight: 5 }}>
							<Price priceInEuroCents={grossPriceCents} />
						</strong>
						<span style={{ color: "grey", fontSize: 10 }}>inkl. MwSt.</span>
						<br />
						<Box
							style={{
								fontSize: 10,
								color: "#505e5f",
							}}
						>
							Lieferzeit: {getDeliveryTimeWorkDaysString(deliveryTimeWorkdays)}
						</Box>
					</Box>
				</Paper>
				<ProductHitStructuredData product={hit} locale={locale} />
			</div>
		</Link>
	)
}

interface Props {
	hit: AlgoliaProductHit
	locale: string
	position: number
	categoryName: string
	listName?: string
}

export default MobileViewItem
